import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ViewServiceBtn from '../common/ViewServiceBtn';
import Options from '../common/Options';
import Modal from '../common/Modal';
import auth from '../../services/authService';
import ServiceDate from '../ServiceDate';
import ServiceSongList from '../ServiceSongList';
import { getRemainingDays, getRemainingDaysLabel } from '../../utils/utilities';
import { extractYouTubeVideoId } from '../../utils/youtube';
import { playlistTracks, watchVideosUrl } from '../../utils/youtubePlaylist';
import PlaylistModal from '../modals/PlaylistModal';
import { isRecentlyUpdated, getRelativeTime } from '../../helpers/songDates';
import { getSongChartCoverage } from '../../utils/partCharts';
import Icon from '../common/Icon';
import ChartProgress from '../common/ChartProgress';
import ServiceLength from '../common/ServiceLength';
import useSongLengths from '../../hooks/useSongLengths';
import { isFullyCharted, totalSeconds } from '../../helpers/serviceLength';
import { ReactComponent as MusicIcon } from '../../icons/album-list.svg';
import { ReactComponent as ChordBarsIcon } from '../../icons/chord-bars.svg';
import { ReactComponent as PlaylistIcon } from '../../icons/playlist-play.svg';
import { ReactComponent as ClockIcon } from '../../icons/clock.svg';
import classNames from 'classnames';
import { formatDate } from '../../utils/format';

import './Service.scss';

const Service = ({ service, songs, onViewService = null, onDelete = null, onEdit = null, editId = null }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = auth.getCurrentUser();
  const remainingDays = getRemainingDays(service?.date);
  const [isPast, setIsPast] = useState(false);
  const [showSongsModal, setShowSongsModal] = useState(false);
  const [showUpdatedModal, setShowUpdatedModal] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const songsData = service.songsIds
    .map((songId) => songs.find((song) => song._id === songId))
    .filter((song) => song !== undefined);
  // The card shows only the total, so no song is loaded until every one is charted
  const songLengths = useSongLengths(songsData.every(isFullyCharted) ? songsData : []);
  const serviceClasses = classNames('service', {
    'service-past': isPast,
  });

  useEffect(() => {
    setIsPast(remainingDays < 0);
  }, [remainingDays]);

  const addRoleCustomClass = (defaultClass, role, customClass) => {
    return role ? `${customClass}` : defaultClass;
  };

  const renderViewService = (item, onViewService) => {
    if (onViewService) return <ViewServiceBtn onViewService={onViewService} service={item} />;
  };

  const renderServiceTag = (tagClass, content) => {
    let classes = 'service__tag';
    classes += ` ${tagClass}`;

    return (
      <span className={classes}>
        <span className="service__tag-text">{content}</span>
      </span>
    );
  };

  const getRemainingDaysTag = () => {
    // A service without a date gets an empty tag
    if (Number.isNaN(remainingDays)) return renderServiceTag('', '');

    let tagClass = 'past';
    if (remainingDays > 1) tagClass = 'days';
    else if (remainingDays === 1) tagClass = 'tomorrow';
    else if (remainingDays === 0) tagClass = 'today';

    return renderServiceTag(tagClass, getRemainingDaysLabel(remainingDays));
  };

  const getSongsCountTag = () => {
    const tagContent = (
      <span className="song-count-tag" title={t('services.viewSongs')} onClick={() => setShowSongsModal(true)}>
        <Icon icon={<MusicIcon />} className="total-count-icon" />
        {songsData.length}
      </span>
    );

    return renderServiceTag('song-count', tagContent);
  };

  // How many of the service's chartable parts are charted, across all its songs
  const getChartProgressTag = () => {
    const coverages = songsData.map(getSongChartCoverage);

    // A song without a count would make the rest read as more finished than the service is
    if (coverages.includes(null)) return null;

    const total = coverages.reduce((sum, coverage) => sum + coverage.total, 0);
    const charted = coverages.reduce((sum, coverage) => sum + coverage.charted, 0);

    // Nothing to say until something is charted
    if (charted === 0) return null;

    return (
      <ChartProgress
        className="chart-progress"
        done={charted}
        total={total}
        label={(percent) => (charted === total ? t('charts.charted') : t('charts.percentCharted', { percent }))}
        doneIcon={<ChordBarsIcon />}
      />
    );
  };

  // The songs edited in the last week, newest first, each with its place in the service
  const updatedSongs = songsData
    .map((song, i) => ({ song, i }))
    .filter(({ song }) => isRecentlyUpdated(song.createdAt, song.contentUpdatedAt))
    .sort((a, b) => new Date(b.song.contentUpdatedAt) - new Date(a.song.contentUpdatedAt));

  // The song view hides the UPDATED badge inside a service, so the latest edit shows here instead
  const getUpdatedTag = () => {
    if (updatedSongs.length === 0) return null;

    return renderServiceTag(
      'updated',
      <span className="updated-tag" onClick={() => setShowUpdatedModal(true)}>
        <ClockIcon aria-hidden="true" />
        {t('services.updated', { time: getRelativeTime(updatedSongs[0].song.contentUpdatedAt) })}
      </span>,
    );
  };

  const renderSongsModal = (isOpen, onClose, title, entries, showUpdated = false) => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={title}>
        <ServiceSongList
          entries={entries}
          showUpdated={showUpdated}
          onSelect={(i) => {
            history.push(i === 0 ? `/view/service/${service._id}` : `/view/service/${service._id}#${i + 1}`);
            onClose();
          }}
        />
      </Modal>
    );
  };

  // Native share sheet on phones; desktop browsers without one get the same text on the clipboard
  const handleShare = async () => {
    const date = formatDate(service.date, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    const songLines = songsData.map((song, i) => `${i + 1}. ${song.title}${song.artist ? ` – ${song.artist}` : ''}`);
    const url = `${window.location.origin}/view/service/${service._id}`;
    const text = [`${service.title} · ${date}`, '', ...songLines].join('\n');

    if (navigator.share) {
      try {
        await navigator.share({ title: service.title, text, url });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(`${text}\n\n${url}`);
      toast.info(t('services.copied'), { autoClose: 2000, hideProgressBar: true });
    } catch (error) {
      toast.error(t('services.copyFailed'));
    }
  };

  // The songs' own videos when they're loaded, so the player can name each one; the service's bare links if not
  const playlist = playlistTracks(songsData);
  const playlistUrl = watchVideosUrl(
    playlist.length
      ? playlist.map((track) => track.videoId)
      : (service.links || []).map(extractYouTubeVideoId).filter(Boolean),
  );

  // A tap plays the service here; a Cmd/Ctrl/Shift or middle click still opens YouTube's playlist
  const handlePlaylistClick = (e) => {
    if (!playlist.length || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setShowPlaylist(true);
  };

  const getYouTubePlaylistTag = () => {
    if (!playlistUrl) return;

    return renderServiceTag(
      'playlist',
      <a href={playlistUrl} target="_blank" rel="noopener noreferrer" onClick={handlePlaylistClick}>
        <PlaylistIcon aria-hidden="true" />
        {t('services.playlist')}
      </a>,
    );
  };

  return (
    <>
      <li className={serviceClasses}>
        <div className="service-flex-container">
          <div className="service-info">
            <div className="service-when">
              <ServiceDate item={service} />
              {getRemainingDaysTag()}
            </div>
            <p className="service-title">{' ' + service.title}</p>
          </div>
          {!editId && (
            <div className={addRoleCustomClass('service-actions', user.isAdmin, 'service-actions-a')}>
              {user && user.isAdmin && (
                <Options
                  item={service}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onPrint={() => history.push(`/print/service/${service._id}`)}
                  onShare={handleShare}
                  editId={editId}
                />
              )}
              {renderViewService(service, onViewService)}
            </div>
          )}
        </div>

        {/* A service that's been and gone keeps just its date and title */}
        {!isPast && (
          <div className="service__tags horizontal-menu">
            {getSongsCountTag()}
            {/* The playlist always comes straight after the song count */}
            {getYouTubePlaylistTag()}
            {getChartProgressTag()}
            <ServiceLength
              seconds={totalSeconds(songsData, songLengths.lengths)}
              fromCache={songLengths.fromCache}
              className="service-length"
            />
            {getUpdatedTag()}
          </div>
        )}
      </li>
      {renderSongsModal(
        showSongsModal,
        () => setShowSongsModal(false),
        service.title,
        songsData.map((song, i) => ({ song, i })),
      )}
      {renderSongsModal(
        showUpdatedModal,
        () => setShowUpdatedModal(false),
        t('services.recentlyUpdated'),
        updatedSongs,
        true,
      )}
      {showPlaylist && <PlaylistModal tracks={playlist} onClose={() => setShowPlaylist(false)} />}
    </>
  );
};

export default Service;
